import { Box, Button, Card, CircularProgress, Typography } from '@mui/material';
import { useGetBlueprintPowerProfile } from 'features/blueprints';
import { PowerProfileChart } from 'greener-ui-library';
import { calculatePowerProfileStats } from 'pages/Blueprints/utils';
import { convertPowerProfileJsonToDataPoints } from 'pages/Blueprints/utils/convertPowerProfileJsonToDataPoints';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Blueprint } from 'types';
import { PowerProfileStatsDisplay } from 'pages/Blueprints/components/PowerProfileStatsDisplay';
import { currencyCodeToLocale } from 'utils/localization';
import { aggregateDataByMinute } from 'pages/Blueprints/NewBlueprint/utils/powerRequirementsUtils';

interface PowerRequirementsBoxProps {
  blueprintDetails?: Blueprint;
  readOnly?: boolean;
}

export const PowerRequirementsBox = ({ blueprintDetails, readOnly = false }: PowerRequirementsBoxProps) => {
  const { t } = useTranslation();

  const { powerProfileData: powerProfileDataFromApi, isLoading } = useGetBlueprintPowerProfile({
    blueprintId: String(blueprintDetails?.blueprintId || ''),
    enabled: !!blueprintDetails?.blueprintId && !readOnly,
  });

  // If readOnly (shared page), use powerProfile from blueprintDetails
  const powerProfileDataFromJson = useMemo(() => {
    if (readOnly && blueprintDetails?.powerProfile) {
      return convertPowerProfileJsonToDataPoints(blueprintDetails.powerProfile);
    }
    return null;
  }, [readOnly, blueprintDetails?.powerProfile]);

  const powerProfileData = powerProfileDataFromJson || powerProfileDataFromApi;
  const chartData1minInterval = useMemo(
    () => (powerProfileData ? aggregateDataByMinute(powerProfileData) : undefined),
    [powerProfileData]
  );

  const { peakPower, averagePower, workingDays, workingHoursStart, workingHoursEnd } =
    calculatePowerProfileStats(chartData1minInterval);

  if (!isLoading && (!chartData1minInterval || chartData1minInterval.length === 0)) {
    return (
      <Card sx={{ padding: 4, borderRadius: '8px' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>{t('blueprints:wizard.steps.powerRequirements')}</Typography>
        <Typography variant="body2" color="text.secondary">{t('blueprints:details.powerProfile.loadError')}</Typography>
      </Card>
    );
  }

  return (
    <Card sx={{ padding: 4, borderRadius: '8px'}}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">{t('blueprints:wizard.steps.powerRequirements')}</Typography>

        {!readOnly && (
          <Button
            data-testid="edit-power-requirements-button"
            component={Link}
            to={`/solutions/${blueprintDetails?.blueprintId}/edit?step=2`}
          >
            {t('blueprints:details.proposedSolution.editButton')}
          </Button>
        )}
      </Box>

      <PowerProfileStatsDisplay
        profileData={chartData1minInterval}
        locale={currencyCodeToLocale(blueprintDetails?.calculations?.[0]?.currencyCode!)}
      />

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="220px">
          <CircularProgress />
        </Box>
      ) : chartData1minInterval && chartData1minInterval.length > 0 ? (
        <Box data-chart-id="power-profile" sx={{ borderRadius: 2, bgcolor: 'grey.50', py: 2 }}>
          <PowerProfileChart
            data={chartData1minInterval}
            height={220}
            peakPowerKw={peakPower}
            averagePowerKw={averagePower}
            workingDays={workingDays}
            workingHoursStart={`${workingHoursStart.toString().padStart(2, '0')}:00`}
            workingHoursEnd={`${workingHoursEnd.toString().padStart(2, '0')}:00`}
            title={t('blueprints:wizard.fields.powerProfile.title')}
            titleStyles={{ ml: 3 }}
            orientationWarningTitle={t('blueprints:wizard.fields.powerProfile.orientationWarningTitle')}
            orientationWarningMessage={t('blueprints:wizard.fields.powerProfile.orientationWarningMessage')}
          />
        </Box>
      ) : (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="220px">
          <Typography variant="body2" color="textSecondary">
            {t('blueprints:details.powerProfile.noData')}
          </Typography>
        </Box>
      )}
    </Card>
  );
};
